import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus } from "lucide-react";
import SEO from "@/components/SEO";
import { FAQ_CATEGORIES_FULL as FAQ_CATEGORIES, FAQ_FLAT_FULL as FAQ_FLAT } from "@/lib/faq";
import { faqPageSchema, breadcrumbSchema } from "@/lib/schemas";
import { FIRM_PHONE, MAIN_OFFICE_PHONE, FIRM_DISCIPLINES } from "@/lib/content";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";

export default function FAQ() {
  const [open, setOpen] = useState(null);
  const toggle = (key) => setOpen((o) => (o === key ? null : key));

  return (
    <div className="bg-ivory" data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions — Texas Business, Trust & Tax Counsel"
        description="Direct answers about Texas business law, Series LLCs, buy-sell agreements, Subchapter V, dynasty trusts, Miller Trusts, physician-practice succession, and cross-border U.S.–India planning from Lone Star Counsel."
        path="/faq"
        schema={[
          faqPageSchema(FAQ_FLAT),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="pt-40 pb-16 max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="kicker">Answers</div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.36em] text-oxblood">{FIRM_DISCIPLINES}</div>
        <h1 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
          Direct answers,<br/>
          <span className="italic font-cormorant text-oxblood">in plain Texan.</span>
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-slate2 font-light leading-relaxed">
          The questions we are asked most often by Texas business owners, physicians, franchise
          investors, and Indian and South Asian families and business owners — answered here for the record.
        </p>
        <div className="mt-10 flex flex-wrap gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/60">Principal Office · Frisco</div>
            <a href={MAIN_OFFICE_PHONE.href} className="mt-1 block font-display text-2xl md:text-3xl text-navy hover:text-oxblood transition" data-testid="faq-phone-main-office">
              {MAIN_OFFICE_PHONE.display}
            </a>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/60">Toll-Free · Statewide</div>
            <a href={FIRM_PHONE.href} className="mt-1 block font-display text-2xl md:text-3xl text-navy hover:text-oxblood transition" data-testid="faq-phone-main">
              {FIRM_PHONE.display}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-32">
        {FAQ_CATEGORIES.map((cat, ci) => (
          <div key={cat.title} className="mt-16 first:mt-0" data-testid={`faq-category-${ci}`}>
            <div className="hairline pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass">{String(ci + 1).padStart(2, "0")} · Section</div>
                <h2 className="mt-3 font-display text-4xl md:text-5xl text-navy leading-[1.0]">{cat.title}</h2>
                <div className="mt-3 font-cormorant italic text-lg text-oxblood">
                  {cat.items.length} {cat.items.length === 1 ? "answer" : "answers"}
                </div>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                {cat.items.map((it, ii) => {
                  const key = `${ci}-${ii}`;
                  const isOpen = open === key;
                  return (
                    <article
                      key={ii}
                      className="border-b-0 py-6"
                      data-testid={`faq-item-${ci}-${ii}`}
                    >
                      <button
                        onClick={() => toggle(key)}
                        aria-expanded={isOpen}
                        className="w-full text-left flex items-start gap-6 group"
                        data-testid={`faq-toggle-${ci}-${ii}`}
                      >
                        <span className="font-cormorant italic text-brass text-2xl shrink-0 w-12">
                          {String(ii + 1).padStart(2, "0")}
                        </span>
                        <h3 className="flex-1 font-display text-xl md:text-2xl text-ink leading-snug group-hover:text-oxblood transition">
                          {it.q}
                        </h3>
                        <span className="shrink-0 mt-1 text-brass">
                          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          className="mt-5 pl-16 pr-8 text-base md:text-lg text-slate2 font-light leading-relaxed animate-fade-in"
                          data-testid={`faq-answer-${ci}-${ii}`}
                        >
                          {it.a}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="mt-24 hairline pt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="kicker">Question we didn&apos;t answer?</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.0] max-w-2xl">
              Write to the firm.<br/>
              <span className="italic font-cormorant text-oxblood">We&apos;ll answer it personally.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-wrap gap-4">
            <Link to="/intake" className="btn-brass" data-testid="faq-cta-intake">Request a Conference</Link>
            <Link to="/contact" className="btn-ghost" data-testid="faq-cta-contact">Write to the Firm</Link>
          </div>
        </div>

        <AttorneyReviewNote reviewed="February 2026" />
      </section>
    </div>
  );
}
